import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Button,
  TextField,
  CircularProgress,
  IconButton,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SyncIcon from '@mui/icons-material/Sync';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { saveAs } from 'file-saver';
import { useTranslation } from 'react-i18next';
import { AlertMessagesContext } from 'react-alert-messages';

import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import Info from 'components/utils/Alert/Info';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import CsvHelper from 'helpers/CsvHelper';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import CashboxReportService from 'services/CashboxReportService';
import TerminalService from 'services/TerminalServices';

const ROWS_PER_PAGE_OPTIONS = [20, 50, 100, 200];

function ManualCashboxOpeningReport() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const { startDate: initialStart, endDate: initialEnd } = dynamicDateRange('MANUAL_CASHBOX_OPENING_SUMMARY');
  const [filterFromDate, setFilterFromDate] = useState(initialStart);
  const [filterToDate, setFilterToDate] = useState(initialEnd);
  const [prevFilterData, setPrevFilterData] = useState({
    filterFromDate: initialStart,
    filterToDate: initialEnd,
  });

  const [terminalsSearchText, setTerminalsSearchText] = useState('');
  const [detailsSearchText, setDetailsSearchText] = useState('');
  const [terminals, setTerminals] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(false);

  const [selectedTerminal, setSelectedTerminal] = useState(null);

  const getTerminalIdFromReport = (item) => item?.terminal?.id ?? null;
  const getTerminalNameFromReport = (item) => item?.terminal?.name ?? '';

  const isReportMatchTerminal = useCallback((reportItem, terminal) => {
    if (!terminal || terminal.id === 'ALL') return true;

    const reportTermId = getTerminalIdFromReport(reportItem);
    const reportTermName = getTerminalNameFromReport(reportItem);

    const termId = terminal.id !== undefined && terminal.id !== null ? String(terminal.id) : null;
    const termName = terminal.name ? String(terminal.name).trim().toLowerCase() : '';

    if (reportTermId && termId && reportTermId === termId) {
      return true;
    }
    if (reportTermName && termName && reportTermName.trim().toLowerCase() === termName) {
      return true;
    }
    return false;
  }, []);

  const fetchData = useCallback(
    async (from = filterFromDate, to = filterToDate) => {
      setLoading(true);
      try {
        const params = {};
        if (from) {
          params.start_date = DateTimeHelpers.getTimezoneOffsetDate(from);
          params.from_date = DateTimeHelpers.getDateYMD(from);
        }
        if (to) {
          params.end_date = DateTimeHelpers.getTimezoneOffsetDate(to);
          params.to_date = DateTimeHelpers.getDateYMD(to);
        }

        const [terminalsRes, reportsRes] = await Promise.allSettled([
          TerminalService.getTerminals(),
          CashboxReportService.getCashboxOpeningReports({}, params),
        ]);

        if (terminalsRes.status === 'fulfilled') {
          const tData = Array.isArray(terminalsRes.value)
            ? terminalsRes.value
            : terminalsRes.value?.data || terminalsRes.value?.results || [];
          setTerminals(tData);
        } else {
          console.error('Failed to fetch terminals:', terminalsRes.reason);
        }

        if (reportsRes.status === 'fulfilled') {
          const rData = Array.isArray(reportsRes.value)
            ? reportsRes.value
            : reportsRes.value?.data || reportsRes.value?.results || [];
          setReports(rData);
        } else {
          console.error('Failed to fetch cashbox report:', reportsRes.reason);
          setReports([]);
          postAlertMessage({
            text: reportsRes.reason?.message || 'Failed to fetch cashbox report',
            type: 'failed',
          });
        }
      } catch (error) {
        console.error('Failed to fetch cashbox data:', error);
        postAlertMessage({ text: error.message || 'Failed to fetch cashbox data', type: 'failed' });
      } finally {
        setLoading(false);
      }
    },
    [filterFromDate, filterToDate, postAlertMessage]
  );

  const generateReport = () => {
    fetchData(filterFromDate, filterToDate);
    setPrevFilterData({
      filterFromDate,
      filterToDate,
    });
    setIsShowGenerateButton(false);
  };

  const isAnyChangeOnReportsFilters = useCallback(() => {
    return (
      filterFromDate !== prevFilterData.filterFromDate ||
      filterToDate !== prevFilterData.filterToDate
    );
  }, [filterFromDate, filterToDate, prevFilterData]);

  useEffect(() => {
    const { startDate, endDate } = dynamicDateRange('MANUAL_CASHBOX_OPENING_SUMMARY');
    fetchData(startDate, endDate);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setIsShowGenerateButton(isAnyChangeOnReportsFilters());
  }, [isAnyChangeOnReportsFilters]);

  const refreshReport = () => {
    fetchData(filterFromDate, filterToDate);
  };

  const terminalSummaries = useMemo(() => {
    const map = new Map();

    (terminals || []).forEach((t) => {
      const key = String(t.id);
      map.set(key, {
        id: t.id,
        name: t.name || `Terminal ${t.id}`,
        mac_id: t.mac_id,
        location: t.location,
        type: t.type,
        count: 0,
      });
    });

    // 2. Count reports and add any unlisted terminals from report data
    (reports || []).forEach((report) => {
      let matched = false;
      for (const [, term] of map.entries()) {
        if (isReportMatchTerminal(report, term)) {
          term.count += 1;
          matched = true;
          break;
        }
      }

      if (!matched) {
        const reportTermId = getTerminalIdFromReport(report);
        const reportTermName = getTerminalNameFromReport(report);
        const fallbackKey = reportTermId ? `term_${reportTermId}` : `term_${reportTermName || 'unassigned'}`;

        if (!map.has(fallbackKey)) {
          map.set(fallbackKey, {
            id: reportTermId || '-',
            name: reportTermName || (reportTermId ? `Terminal ${reportTermId}` : tCommon('Unassigned') || 'Unassigned'),
            count: 0,
          });
        }
        map.get(fallbackKey).count += 1;
      }
    });

    return Array.from(map.values());
  }, [terminals, reports, isReportMatchTerminal, tCommon]);

  const filteredTerminals = useMemo(() => {
    if (!terminalsSearchText) return terminalSummaries;
    const lower = terminalsSearchText.toLowerCase();
    return terminalSummaries.filter((t) => {
      const name = (t.name || '').toLowerCase();
      const id = String(t.id || '').toLowerCase();
      const location = (t.location || '').toLowerCase();
      return name.includes(lower) || id.includes(lower) || location.includes(lower);
    });
  }, [terminalSummaries, terminalsSearchText]);

  // Reports filtered for the selected terminal (or all if ALL selected)
  const currentTerminalReports = useMemo(() => {
    if (!selectedTerminal || selectedTerminal.id === 'ALL') {
      return reports;
    }
    return (reports || []).filter((r) => isReportMatchTerminal(r, selectedTerminal));
  }, [reports, selectedTerminal, isReportMatchTerminal]);

  // Filtered detail reports based on search
  const filteredDetailReports = useMemo(() => {
    if (!detailsSearchText) return currentTerminalReports;
    const lower = detailsSearchText.toLowerCase();
    return (currentTerminalReports || []).filter((r) => {
      const terminal = (
        r?.terminal?.name ||
        r?.terminal_name ||
        r?.terminal ||
        ''
      )
        .toString()
        .toLowerCase();
      const remark = (r?.remark || r?.reason || '').toString().toLowerCase();
      const id = String(r?.id || '').toLowerCase();
      return terminal.includes(lower) || remark.includes(lower) || id.includes(lower);
    });
  }, [currentTerminalReports, detailsSearchText]);

  // Terminal list headers
  const terminalHeaders = [
    {
      label: tCommon('ID'),
      id: 'id',
      type: 'text',
    },
    {
      label: tCommon('Terminal'),
      id: 'name',
      type: 'text',
    },
    {
      label: tCommon('Count'),
      id: 'count',
      type: 'callback',
      viewRender: (item) => (
        <span
          style={{
            fontWeight: 600,
            fontSize: '14px',
            color: item.count > 0 ? '#0d2d7e' : '#757575',
            backgroundColor: item.count > 0 ? '#e8f0fe' : '#f5f5f5',
            padding: '4px 12px',
            borderRadius: '12px',
            display: 'inline-block',
          }}
        >
          {item.count}
        </span>
      ),
    },
    {
      label: tCommon('Actions') ,
      id: 'actions',
      type: 'callback',
      viewRender: (item) => (
        <Tooltip title={tCommon('View Details')}>
          <IconButton
            size="small"
            color="primary"
            onClick={() => {
              setSelectedTerminal(item);
              setDetailsSearchText('');
            }}
            style={{ padding: '6px' }}
          >
            <RemoveRedEyeIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const detailHeaders = useMemo(() => {
    const headers = [
      {
        label: tCommon('ID'),
        id: 'id',
        type: 'text',
      },
      {
        label: tCommon('Date & Time'),
        id: 'created_at',
        type: 'callback',
        viewRender: (item) => DateTimeHelpers.formatUTCDateTime(item.created_at) || '-',
      },
    ];

    if (selectedTerminal?.id === 'ALL') {
      headers.push({
        label: tCommon('Terminal'),
        id: 'terminal',
        type: 'callback',
        viewRender: (item) =>
          item?.terminal?.name ||
          item?.terminal_name ||
          (typeof item?.terminal === 'object' ? item?.terminal?.id : item?.terminal) ||
          '-',
      });
    }

    headers.push({
      label: tReport('Remark'),
      id: 'remark',
      type: 'callback',
      viewRender: (item) => item?.remark || '-',
    });

    return headers;
  }, [selectedTerminal, tCommon, tReport]);

  // Export terminals summary to CSV
  const exportTerminalsSummaryCsv = () => {
    const csvColumns = {
      id: {
        title: tCommon('ID'),
        getValue: (row) => row.id,
      },
      name: {
        title: tCommon('Terminal'),
        getValue: (row) => row.name,
      },
      count: {
        title: tCommon('Count'),
        getValue: (row) => row.count,
      },
    };
    const csv = CsvHelper.getString(filteredTerminals, csvColumns);
    const blob = new Blob([csv], { type: 'text/csv' });
    const dateTime = DateTimeHelpers.getDate(new Date());
    saveAs(blob, `MANUAL-CASHBOX-TERMINALS-SUMMARY-${dateTime}.csv`);

    postAlertMessage({
      text: tReport('Exported to excel successfully') ,
      type: 'success',
    });
  };

  // Export detail openings to CSV (as like current report)
  const exportDetailReportsCsv = () => {
    const csvColumns = {
      date_time: {
        title: tCommon('Date & Time'),
        getValue: (row) => DateTimeHelpers.formatUTCDateTime(row.created_at) || '-',
      },
      ...(selectedTerminal?.id === 'ALL'
        ? {
            terminal: {
              title: tCommon('Terminal'),
              getValue: (row) => row?.terminal?.name || '-',
            },
          }
        : {}),
      remark: {
        title: tReport('Remark'),
        getValue: (row) => row?.remark || '-',
      },
    };
    const csv = CsvHelper.getString(filteredDetailReports, csvColumns);
    const blob = new Blob([csv], { type: 'text/csv' });
    const dateTime = DateTimeHelpers.getDate(new Date());
    const termSuffix =
      selectedTerminal?.id === 'ALL'
        ? '-ALL'
        : selectedTerminal?.name
        ? `-${selectedTerminal.name.replace(/\s+/g, '_')}`
        : '';
    saveAs(blob, `MANUAL-CASHBOX-REPORT${termSuffix}-${dateTime}.csv`);

    postAlertMessage({
      text: tReport('Exported to excel successfully') || 'Exported to excel successfully',
      type: 'success',
    });
  };

  const totalOpeningsCount = reports.length;
  const terminalsWithOpeningsCount = terminalSummaries.filter((t) => t.count > 0).length;

  return (
    <div className={styles.contentWrapper}>
      {/* Page Title & Navigation */}
      <div className={styles.titleSec} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {selectedTerminal && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setSelectedTerminal(null)}
              startIcon={<ArrowBackRoundedIcon />}
              size="small"
              style={{ textTransform: 'none', fontWeight: 500 }}
            >
              {tCommon('Back')}
            </Button>
          )}
          <span className={styles.title}>
            {tCommon('Manual Cashbox Report')}
            {selectedTerminal && (
              <span className={styles.menuTitle}>/ {selectedTerminal.name || tCommon('Details')}</span>
            )}
          </span>
        </div>

        {/* Action shortcut to View All Openings when in terminal summary view */}
        {!selectedTerminal && (
          <Button
            variant="text"
            color="primary"
            onClick={() => {
              setSelectedTerminal({ id: 'ALL', name: tCommon('All Terminals')});
              setDetailsSearchText('');
            }}
            startIcon={<LockOpenIcon fontSize="small" />}
            style={{ textTransform: 'none', fontWeight: 500 }}
          >
            {tCommon('View All Openings')} ({totalOpeningsCount})
          </Button>
        )}
      </div>

      {/* Summary KPI Cards (shown on main terminals page) */}
      {!selectedTerminal && (
        <div className={styles.summaryContainer} style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
          <div className={`${styles.summaryCard} ${styles.cardBlue}`}>
            <div className={styles.cardTitle}>{tCommon('Total Terminals')}</div>
            <div className={styles.cardValue}>
              <PointOfSaleIcon style={{ verticalAlign: 'middle', marginRight: '8px', color: '#383fc1' }} fontSize="small" />
              {terminalSummaries.length}
            </div>
            <div className={styles.cardDetails}>
              <span>{tCommon('Active in system')}</span>
            </div>
          </div>

          <div className={`${styles.summaryCard} ${styles.cardGreen}`}>
            <div className={styles.cardTitle}>{tCommon('Cashbox Openings') }</div>
            <div className={styles.cardValue}>
              <LockOpenIcon style={{ verticalAlign: 'middle', marginRight: '8px', color: '#2e7d32' }} fontSize="small" />
              {totalOpeningsCount}
            </div>
            <div className={styles.cardDetails}>
              <span>{tCommon('Across all terminals')}</span>
            </div>
          </div>

          <div className={`${styles.summaryCard} ${styles.cardOrange}`}>
            <div className={styles.cardTitle}>{tCommon('Active Terminals with Openings')}</div>
            <div className={styles.cardValue}>
              {terminalsWithOpeningsCount} / {terminalSummaries.length}
            </div>
            <div className={styles.cardDetails}>
              <span>{tCommon('Terminals opened at least once')}</span>
            </div>
          </div>
        </div>
      )}

      {/* Filter and Action Controls */}
      <div className={styles.changeable}>
        <div className={styles.filterSec}>
          <div
            className={styles.filerInputSec}
            style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', flexWrap: 'wrap' }}
          >
            <DateTimeRange
              setFilterFromDate={setFilterFromDate}
              setFilterToDate={setFilterToDate}
              storageKey="MANUAL_CASHBOX_OPENING_SUMMARY"
            />

            {isShowGenerateButton && (
              <Button
                variant="contained"
                color="success"
                onClick={generateReport}
                disabled={loading}
                style={{ height: '40px', whiteSpace: 'nowrap' }}
              >
                {tCommon('Generate Report')}
              </Button>
            )}

            {selectedTerminal && (
              <FormControl size="small" style={{ minWidth: 180 }}>
                <InputLabel id="terminal-select-label">{tCommon('Terminal')}</InputLabel>
                <Select
                  labelId="terminal-select-label"
                  value={selectedTerminal.id}
                  label={tCommon('Terminal')}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === 'ALL') {
                      setSelectedTerminal({ id: 'ALL', name: tCommon('All Terminals') });
                    } else {
                      const found = terminalSummaries.find((t) => String(t.id) === String(val));
                      if (found) setSelectedTerminal(found);
                    }
                  }}
                >
                  <MenuItem value="ALL">{tCommon('All Terminals')}</MenuItem>
                  {terminalSummaries.map((term) => (
                    <MenuItem key={term.id} value={term.id}>
                      {term.name} ({term.count})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {!selectedTerminal ? (
              <TextField
                size="small"
                placeholder={tCommon('Search Terminals')}
                value={terminalsSearchText}
                onChange={(e) => setTerminalsSearchText(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon fontSize="small" sx={{ color: 'gray', mr: 1 }} />,
                }}
              />
            ) : (
              <TextField
                size="small"
                placeholder={tCommon('Search')}
                value={detailsSearchText}
                onChange={(e) => setDetailsSearchText(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon fontSize="small" sx={{ color: 'gray', mr: 1 }} />,
                }}
              />
            )}

            <Button
              variant="outlined"
              color="primary"
              onClick={refreshReport}
              disabled={loading}
              startIcon={<SyncIcon />}
            >
              {tCommon('Sync')}
            </Button>

            {!selectedTerminal ? (
              <Button
                variant="contained"
                color="primary"
                onClick={exportTerminalsSummaryCsv}
                disabled={loading || !filteredTerminals.length}
                startIcon={<ImportExportIcon />}
              >
                {tCommon('Export')}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={exportDetailReportsCsv}
                disabled={loading || !filteredDetailReports.length}
                startIcon={<ImportExportIcon />}
              >
                {tCommon('Export')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className={styles.changeable} style={{ marginTop: '16px' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <CircularProgress />
          </div>
        ) : !selectedTerminal ? (
          filteredTerminals.length === 0 ? (
            <Info message={tCommon('No terminals found.')}/>
          ) : (
            <DataTable
              columns={terminalHeaders}
              rows={filteredTerminals}
              rowKey="id"
              isLoading={loading}
              pagination={true}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            />
          )
        ) : (
          filteredDetailReports.length === 0 ? (
            <Info
              message={
                tCommon('No records found for the selected date range.') ||
                'No records found for the selected date range.'
              }
            />
          ) : (
            <DataTable
              columns={detailHeaders}
              rows={filteredDetailReports}
              rowKey="id"
              isLoading={loading}
              pagination={true}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            />
          )
        )}
      </div>
    </div>
  );
}

export default withConsoleBase(ManualCashboxOpeningReport);

